import { useContext } from 'react';
import AuthContext from '../contexts/AuthContext';

/**
 * 認証コンテキストへアクセスするためのカスタムフック
 * 
 * 意図：
 * - AuthContextへの簡単なアクセスを提供
 * - 型安全性の確保
 * - エラーハンドリングの一元化
 * 
 * 注意点：
 * - AuthProvider内でのみ使用可能
 * - コンテキスト外での使用時にエラーをスロー
 * 
 * 改善提案：
 * - 追加のユーティリティ関数
 * - ロール別のアクセス制御関数
 */

export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuthはAuthProvider内で使用する必要があります');
  }
  
  return context;
};

// 便利なユーティリティ関数

/**
 * ユーザーが特定のロールを持っているか確認
 * @param role チェックするロール
 * @returns ロールを持っているかどうか
 */
export const useHasRole = (role: 'admin' | 'doctor' | 'operator') => {
  const { user } = useAuth();
  return user?.role === role;
};

/**
 * ユーザーが管理者かどうか確認
 * @returns 管理者かどうか
 */
export const useIsAdmin = () => {
  return useHasRole('admin');
};

/**
 * ユーザーが医師かどうか確認
 * @returns 医師かどうか
 */
export const useIsDoctor = () => {
  return useHasRole('doctor');
};

/**
 * ユーザーがオペレーターかどうか確認
 * @returns オペレーターかどうか
 */
export const useIsOperator = () => {
  return useHasRole('operator');
};

export default useAuth;